import React from 'react';
import { Link } from 'react-router-dom';
import { TriangleAlert, Trophy } from 'lucide-react';
import {
    ChallengeStatus,
    UserChallengeSupportDto,
    UserCommunitySupportDto
} from '@api/ChallengeClient';
import { Badge } from '@components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { I18n } from '@core/i18n';
import { cn } from '@core/utils/cn';
import { links } from '../../../../core/router';
import { challengeStatusVariant, describeLastSync, formatJoined } from './format';
import { ListHeading, ShowAllButton, PREVIEW_COUNT } from './list-section';

interface ChallengesTableProps {
    challenges: UserChallengeSupportDto[];
    communities: UserCommunitySupportDto[];
}

export const UnverifiedBadge = () => (
    <Badge variant="warning" title="Not a verified member of this community">
        Unverified
    </Badge>
);

export const ChallengesTable = ({ challenges, communities }: ChallengesTableProps) => {
    const [expanded, setExpanded] = React.useState(false);
    const verifiedCommunityIds = new Set(
        communities.filter((community) => community.verified).map((community) => community.id)
    );
    const visible = expanded ? challenges : challenges.slice(0, PREVIEW_COUNT);

    return (
        <div className="flex flex-col">
            <ListHeading label="Challenges" count={challenges.length}>
                {challenges.length > 0 && (
                    <span className="ml-auto font-normal normal-case tracking-normal">
                        In progress first, then most recently joined
                    </span>
                )}
            </ListHeading>
            {challenges.length === 0 ? (
                <p className="py-3 text-sm text-muted-foreground">
                    Hasn't joined any challenge
                </p>
            ) : (
                <Table className="min-w-[820px]">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Challenge</TableHead>
                            <TableHead>Community</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Rank</TableHead>
                            <TableHead className="text-right">Points</TableHead>
                            <TableHead>Joined</TableHead>
                            <TableHead>Last sync</TableHead>
                            <TableHead className="text-right">Won</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {visible.map((challenge) => {
                            const inProgress =
                                challenge.status === ChallengeStatus.IN_PROGRESS;
                            const sync = describeLastSync(
                                challenge.lastSyncTimestamp,
                                inProgress
                            );
                            const dimmed =
                                challenge.status === ChallengeStatus.CANCELED ||
                                challenge.status === ChallengeStatus.DRAFT;
                            return (
                                <TableRow
                                    key={challenge.id}
                                    className={cn(dimmed && 'opacity-70')}
                                >
                                    <TableCell className="max-w-xs">
                                        <Link
                                            to={links.communityChallengeLeaderboard(
                                                String(challenge.communityId),
                                                String(challenge.id)
                                            )}
                                            className={cn(
                                                'line-clamp-2 hover:text-primary',
                                                inProgress && 'font-medium'
                                            )}
                                        >
                                            {I18n.i18nTextToStringConverter(challenge.name)}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {!challenge.communityId ? (
                                            <span className="text-muted-foreground">
                                                User duel
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-2">
                                                <Link
                                                    to={links.communityDetails(
                                                        String(challenge.communityId)
                                                    )}
                                                    className="truncate text-muted-foreground hover:text-primary"
                                                >
                                                    {challenge.communityName}
                                                </Link>
                                                {!verifiedCommunityIds.has(challenge.communityId) && (
                                                    <UnverifiedBadge />
                                                )}
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={challengeStatusVariant(challenge.status)}>
                                            {challenge.status.replace('_', ' ')}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right font-semibold tabular-nums">
                                        {challenge.position > 0 ? `#${challenge.position}` : '–'}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {challenge.points.toLocaleString('en-US')}
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap text-muted-foreground">
                                        {formatJoined(challenge.joinedDate)}
                                    </TableCell>
                                    <TableCell
                                        className={cn(
                                            'whitespace-nowrap',
                                            sync.warning
                                                ? 'font-medium text-warning'
                                                : 'text-muted-foreground'
                                        )}
                                    >
                                        <span className="flex items-center gap-1.5">
                                            {sync.warning && <TriangleAlert className="size-3.5" />}
                                            {sync.label}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {challenge.won && (
                                            <Badge variant="default">
                                                <Trophy /> WON
                                            </Badge>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
            <ShowAllButton total={challenges.length} expanded={expanded} onToggle={() => setExpanded((value) => !value)} />
        </div>
    );
};
